import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Cpu,
  Cloud,
  GearSix,
  ChartLineUp,
  Buildings,
  Factory,
  Mountains,
  Plant,
  GraduationCap,
  Compass,
  Target,
  Blueprint as BlueprintIcon,
  Stack,
} from "@phosphor-icons/react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1735494034625-86409b829b33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxzbWFydCUyMG1hbnVmYWN0dXJpbmclMjBmYWN0b3J5JTIwd29ya2Vyc3xlbnwwfHx8fDE3ODMxNzIxMzV8MA&ixlib=rb-4.1.0&q=85";

const metrics = [
  { value: "30%", label: "Operational Efficiency Improvement", tint: "surface-blue", accent: "border-t-4 border-blue-600", num: "text-blue-700" },
  { value: "50+", label: "AI Use Cases Deployed", tint: "surface-amber", accent: "border-t-4 border-amber-500", num: "text-amber-700" },
  { value: "20+", label: "Industry Solutions", tint: "surface-emerald", accent: "border-t-4 border-emerald-600", num: "text-emerald-700" },
  { value: "100+", label: "Processes Digitized", tint: "surface-rose", accent: "border-t-4 border-rose-500", num: "text-rose-700" },
];

const pillars = [
  {
    icon: Cpu,
    title: "AI & Generative AI",
    body: "Predictive analytics, computer vision, NLP, and autonomous decision support engineered for industrial complexity.",
    tint: "bg-blue-50",
    icon_bg: "bg-blue-600",
    label_bg: "bg-blue-600",
  },
  {
    icon: Cloud,
    title: "Cloud & Data",
    body: "Cloud-native foundations, hybrid architectures, and modern data platforms that unify siloed operations.",
    tint: "bg-amber-50",
    icon_bg: "bg-amber-500",
    label_bg: "bg-amber-500",
  },
  {
    icon: GearSix,
    title: "Intelligent Automation",
    body: "Workflow automation, digital twins, and unified command centers that keep operations running 24×7.",
    tint: "bg-emerald-50",
    icon_bg: "bg-emerald-600",
    label_bg: "bg-emerald-600",
  },
];

const industries = [
  { icon: Factory, name: "Metals & Steel", href: "/solutions#metals", color: "rose" },
  { icon: Mountains, name: "Mining", href: "/solutions#mining", color: "amber" },
  { icon: BlueprintIcon, name: "Construction", href: "/solutions#construction", color: "orange" },
  { icon: GearSix, name: "Manufacturing", href: "/solutions#manufacturing", color: "blue" },
  { icon: Buildings, name: "Realty & Facilities", href: "/solutions#realty", color: "teal" },
  { icon: GraduationCap, name: "Education", href: "/solutions#education", color: "violet" },
  { icon: Plant, name: "Agriculture", href: "/solutions#agriculture", color: "lime" },
];

const industryColorMap = {
  rose: "bg-rose-50 border-rose-500 text-rose-700 hover:bg-rose-500",
  amber: "bg-amber-50 border-amber-500 text-amber-700 hover:bg-amber-500",
  orange: "bg-orange-50 border-orange-500 text-orange-700 hover:bg-orange-500",
  blue: "bg-blue-50 border-blue-600 text-blue-700 hover:bg-blue-600",
  teal: "bg-teal-50 border-teal-600 text-teal-700 hover:bg-teal-600",
  violet: "bg-violet-50 border-violet-600 text-violet-700 hover:bg-violet-600",
  lime: "bg-lime-50 border-lime-600 text-lime-700 hover:bg-lime-600",
};

const blueprintSteps = [
  { n: "01", title: "Domain Expertise", desc: "Deep industrial context tailored to complex realities.", accent: "bg-blue-600" },
  { n: "02", title: "Cloud-Native Foundation", desc: "Unified data integration on a scalable backbone.", accent: "bg-amber-500" },
  { n: "03", title: "AI-First Engine", desc: "Predictive and generative capabilities embedded into workflow.", accent: "bg-emerald-600" },
  { n: "04", title: "Measurable Outcomes", desc: "Operational ROI, productivity, and growth — quantified.", accent: "bg-rose-500" },
];

const platforms = [
  { name: "AI Manufacturing Suite", accent: "bg-blue-600" },
  { name: "AI Mining Platform", accent: "bg-amber-500" },
  { name: "Smart Facility Platform", accent: "bg-teal-600" },
  { name: "AI Procurement Portal", accent: "bg-emerald-600" },
  { name: "AI Command Center", accent: "bg-rose-500" },
  { name: "Intelligent Analytics Platform", accent: "bg-violet-600" },
];

const megaMenuItems = [
  { name: "LeadscoringBizlem", href: "https://leadscoring.bizlem.com/", host: "leadscoring.bizlem.com", icon: Target, tint: "bg-blue-50", accent: "bg-blue-600", text: "text-blue-700" },
  { name: "SmartCampus", href: "https://smartcampus.bizlem.com/", host: "smartcampus.bizlem.com", icon: GraduationCap, tint: "bg-violet-50", accent: "bg-violet-600", text: "text-violet-700" },
  { name: "Metals", href: "https://metals.bizlem.com/", host: "metals.bizlem.com", icon: Factory, tint: "bg-rose-50", accent: "bg-rose-500", text: "text-rose-700" },
  { name: "Agriculture", href: "https://agriculture.bizlem.com/", host: "agriculture.bizlem.com", icon: Plant, tint: "bg-emerald-50", accent: "bg-emerald-600", text: "text-emerald-700" },
  { name: "Mining", href: "https://mining.bizlem.com/", host: "mining.bizlem.com", icon: Mountains, tint: "bg-amber-50", accent: "bg-amber-500", text: "text-amber-700" },
  { name: "Realty", href: "https://realty.bizlem.com/", host: "realty.bizlem.com", icon: Buildings, tint: "bg-teal-50", accent: "bg-teal-600", text: "text-teal-700" },
  { name: "AI Services", href: "https://aiservices.bizlem.com/", host: "aiservices.bizlem.com", icon: Cpu, tint: "bg-sky-50", accent: "bg-sky-600", text: "text-sky-700" },
  { name: "Precast", href: "https://precast.bizlem.com/", host: "precast.bizlem.com", icon: BlueprintIcon, tint: "bg-orange-50", accent: "bg-orange-500", text: "text-orange-700" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative border-b border-zinc-200 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-lines pointer-events-none opacity-70" />
        <div className="container-x mx-auto pt-16 lg:pt-24 pb-16 lg:pb-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 border-2 border-blue-500 bg-blue-50 px-3 py-1.5 mb-8">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-overline text-blue-700">Industrial Intelligence — Live</span>
              </div>
              <h1
                data-testid="hero-headline"
                className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.9] text-zinc-950"
              >
                Industrial<br />
                Intelligence.<br />
                <span className="text-zinc-400">Real Results.</span>
              </h1>
              <p className="mt-8 text-lg sm:text-xl leading-relaxed text-zinc-600 max-w-2xl">
                Bizlem partners with heavy industries to modernize operations and unlock actionable
                insight through AI, Cloud, and Intelligent Automation — a proven blueprint for
                measurable business results.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-book-consultation-btn"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-4 text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md"
                >
                  Book a Consultation <ArrowUpRight size={18} weight="bold" />
                </Link>
                <Link
                  to="/platforms"
                  data-testid="hero-request-demo-btn"
                  className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-700 px-6 py-4 text-sm font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                >
                  Request a Demo
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative border border-zinc-200 bg-white">
                <img
                  src={HERO_IMG}
                  alt="Smart manufacturing operations"
                  className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover"
                  loading="eager"
                />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-2 border-l-4 border-emerald-500 border-t border-r border-b border-zinc-200">
                  <div className="text-overline text-emerald-700">● Live Feed</div>
                  <div className="text-sm font-bold text-zinc-950">Plant 04 · Shift B</div>
                </div>
                <div className="absolute bottom-4 right-4 bg-blue-600 text-white px-4 py-3 shadow-lg">
                  <div className="text-[10px] font-mono tracking-widest text-blue-200">EFFICIENCY</div>
                  <div className="text-2xl font-black" style={{ fontFamily: "Chivo" }}>+30.2%</div>
                </div>
              </div>
            </div>
          </div>

          {/* METRICS BAR */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m, i) => (
              <div
                key={m.label}
                data-testid={`hero-metric-${i}`}
                className={`p-6 lg:p-8 ${m.tint} ${m.accent} shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className={`text-4xl lg:text-5xl font-black tracking-tighter ${m.num}`} style={{ fontFamily: "Chivo" }}>
                  {m.value}
                </div>
                <div className="mt-2 text-sm text-zinc-700 font-medium max-w-[220px]">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="border-b border-zinc-200" data-testid="pillars-section">
        <div className="container-x mx-auto py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4">The Three Pillars</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
                Empowering the modern enterprise with next-generation capabilities.
              </h2>
            </div>
            <p className="lg:col-span-7 text-lg text-zinc-600 max-w-2xl">
              An integrated foundation of AI, Cloud, and Intelligent Automation — designed for
              heavy industries and complex operations.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {pillars.map((p) => (
              <div
                key={p.title}
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={`p-8 lg:p-10 ${p.tint} border border-zinc-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group`}
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 ${p.icon_bg} text-white`}>
                  <p.icon size={28} weight="light" />
                </div>
                <div className={`mt-6 inline-block ${p.label_bg} text-white text-[10px] font-bold tracking-widest uppercase px-2 py-1`}>
                  Pillar
                </div>
                <h3 className="mt-3 text-xl font-bold">{p.title}</h3>
                <p className="mt-3 text-zinc-700 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEGA MENU — Bizlem product sub-domains */}
      <section className="border-b border-zinc-200 bg-white" data-testid="mega-menu-section">
        <div className="container-x mx-auto py-20 lg:py-24">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-overline mb-3 text-blue-700">Bizlem Product Suite</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                Explore the full <span className="highlight-marker-blue">Bizlem universe.</span>
              </h2>
            </div>
            <p className="text-sm text-zinc-600 max-w-md">
              Jump directly into any of our specialized platforms — each purpose-built for its industry.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {megaMenuItems.map((m) => (
              <a
                key={m.name}
                href={m.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`mega-menu-link-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group ${m.tint} border-2 border-transparent hover:border-current ${m.text} p-6 flex flex-col gap-4 hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
              >
                <div className="flex items-start justify-between">
                  <div className={`inline-flex items-center justify-center w-12 h-12 ${m.accent} text-white shadow-sm`}>
                    <m.icon size={22} weight="light" />
                  </div>
                  <ArrowUpRight size={18} weight="bold" className="opacity-40 group-hover:opacity-100 transition" />
                </div>
                <div>
                  <div className="text-lg font-bold text-zinc-950">{m.name}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1 truncate">{m.host}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES MARQUEE */}
      <section className="border-b border-zinc-200 bg-zinc-50 overflow-hidden" data-testid="industries-section">
        <div className="container-x mx-auto py-16">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <div className="text-overline mb-3">Industrial Footprint</div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tighter">
                Intelligence at the edge of every industry.
              </h2>
            </div>
            <Link
              to="/solutions"
              data-testid="industries-view-all"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold link-underline"
            >
              View all solutions <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="relative py-8 border-y border-zinc-200 bg-white">
          <div className="flex gap-16 animate-marquee whitespace-nowrap">
            {[...industries, ...industries].map((ind, i) => (
              <div key={i} className="inline-flex items-center gap-3 text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950" style={{ fontFamily: "Chivo" }}>
                <ind.icon size={32} weight="light" className="text-zinc-400" />
                {ind.name}
                <span className="text-zinc-300">/</span>
              </div>
            ))}
          </div>
        </div>

        <div className="container-x mx-auto py-12 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {industries.map((ind) => {
            const cls = industryColorMap[ind.color];
            return (
              <Link
                key={ind.name}
                to={ind.href}
                data-testid={`industry-tile-${ind.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={`p-6 flex flex-col items-start gap-3 border-l-4 hover:text-white transition-all duration-300 group ${cls}`}
              >
                <ind.icon size={24} weight="light" className="group-hover:text-white transition" />
                <div className="text-sm font-semibold text-zinc-900 group-hover:text-white">{ind.name}</div>
                <ArrowUpRight size={14} weight="bold" className="opacity-0 group-hover:opacity-100 transition text-white" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* BLUEPRINT TEASER */}
      <section className="border-b border-zinc-200 bg-zinc-100" data-testid="blueprint-teaser">
        <div className="container-x mx-auto py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4">The Blueprint</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
                A proven methodology for industrial intelligence.
              </h2>
              <p className="mt-6 text-lg text-zinc-600">
                Four disciplined steps — from deep domain context to measurable business ROI.
              </p>
              <Link
                to="/blueprint"
                data-testid="blueprint-teaser-cta"
                className="inline-flex items-center gap-2 mt-8 text-sm font-semibold link-underline"
              >
                Explore the Blueprint <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {blueprintSteps.map((s) => (
                <div key={s.n} className="bg-white border border-zinc-200 p-8 relative overflow-hidden hover:shadow-lg transition-shadow group">
                  <div className={`absolute top-0 left-0 h-1 w-full ${s.accent}`} />
                  <div className={`inline-flex items-center justify-center w-10 h-10 ${s.accent} text-white font-black text-sm`} style={{ fontFamily: "Chivo" }}>
                    {s.n}
                  </div>
                  <h3 className="mt-4 text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-sm text-zinc-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS TEASER */}
      <section className="border-b border-zinc-200" data-testid="platforms-teaser">
        <div className="container-x mx-auto py-20 lg:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-overline mb-3">Featured Platforms</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
                Engineered for rapid implementation and time-to-value.
              </h2>
            </div>
            <Link
              to="/platforms"
              data-testid="platforms-teaser-cta"
              className="inline-flex items-center gap-2 border border-zinc-950 px-5 py-3 text-sm font-semibold hover:bg-zinc-950 hover:text-white transition-colors"
            >
              All Platforms <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((p, i) => (
              <Link
                key={p.name}
                to="/platforms"
                data-testid={`platform-teaser-${i}`}
                className="bg-white border border-zinc-200 p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                <div className={`absolute top-0 left-0 h-1 w-full ${p.accent}`} />
                <div className="flex items-start justify-between">
                  <div className={`inline-flex items-center justify-center w-12 h-12 ${p.accent} text-white`}>
                    <Stack size={22} weight="light" />
                  </div>
                  <span className="text-overline text-zinc-400">0{i + 1}</span>
                </div>
                <h3 className="mt-8 text-xl font-bold">{p.name}</h3>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 group-hover:text-zinc-950 transition">
                  Explore <ArrowUpRight size={14} weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-b border-zinc-200 bg-amber-50 relative overflow-hidden" data-testid="testimonial-section">
        <div className="absolute -top-8 -left-4 text-[240px] font-black text-amber-200 leading-none select-none pointer-events-none" style={{ fontFamily: "Chivo" }}>“</div>
        <div className="container-x mx-auto py-20 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-2 text-overline text-amber-700">
              Validated by Leaders.<br />Powered by the Best.
            </div>
            <div className="lg:col-span-10">
              <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-zinc-950">
                “Bizlem helped us modernize operations and unlock actionable insights through AI
                and cloud technologies. <span className="highlight-marker">Digital transformation with measurable business results.</span>”
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-500" />
                <div>
                  <div className="font-bold text-zinc-950">Industrial Enterprise Partner</div>
                  <div className="text-sm text-zinc-700">Digital Transformation Program</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white" data-testid="final-cta">
        <div className="container-x mx-auto py-24 lg:py-32">
          <div className="relative border-l-8 border-blue-600 bg-blue-50 p-10 lg:p-16">
            <div className="absolute top-6 right-6 hidden lg:block text-overline text-blue-700">The Intelligent Enterprise</div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <div className="text-overline mb-4 text-blue-700 lg:hidden">The Intelligent Enterprise</div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
                  Ready to build the<br /><span className="highlight-marker-blue">intelligent enterprise?</span>
                </h2>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="final-cta-book-btn"
                  className="inline-flex items-center justify-between gap-2 bg-blue-600 text-white px-6 py-4 text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md"
                >
                  Book a Consultation <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/blueprint"
                  data-testid="final-cta-blueprint-btn"
                  className="inline-flex items-center justify-between gap-2 border-2 border-blue-600 text-blue-700 bg-white px-6 py-4 text-sm font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                >
                  Explore the Blueprint <Compass size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
